<template>
  <div class="fts-module">
    <template v-for="(x, ind) in menus">
      <!-- module (group, menu) -->
      <div
        class="module-wrapper module-group"
        :class="{ open: x.open }"
        :style="moduleChild"
        :key="x.id"
        @click="openChildren(x)"
        @mouseover="onHover(x, true)"
        @mouseleave="onHover(x)"
        v-if="isGroup(x)"
      >
        <div class="module-info">
          <!-- emoji -->
          <fts-moji :size="20" :emoji="x.emoji" class="module-emoji" />
          <!-- name -->
          <fts-tippy
            trigger-class="module-name"
            :options="{arrow: false, placement: 'top-start'}"
            :content="x.name"
            class="module-name-wrapper"
          >
            <template #trigger>{{x.name}}</template>
          </fts-tippy>
          <!-- profile -->
          <div class="module-tag" v-if="x.isProfile">
            <font-awesome-icon :icon="['fas', 'user']" fixed-width />
          </div>
        </div>
        <!-- actions -->
        <div class="module-action-container">
          <!-- design -->
          <router-link :to="designRoute(x.module)" custom v-slot="{ href }">
            <fts-btn
              sm
              icon
              color="secondary"
              class="module-action stopPropagation"
              box-shadow-none
              tag="a"
              :href="href"
              v-if="isAdminUser && mode != 'design' && x.module && x.hover"
            >
              <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
            </fts-btn>
          </router-link>
          <!-- create -->
          <fts-btn 
            sm 
            icon 
            color="primary" 
            class="module-action stopPropagation" 
            box-shadow-none
            @click="openCreateForm(x)"
            v-if="creatable(x.id) && mode != 'design' && x.hover"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
          </fts-btn>
        </div>
        <!-- toggle -->
        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width class="module-toggle" />
      </div>
      <!-- custom (script) -->
      <div 
        class="module-wrapper"
        :style="moduleChild"
        :key="x.id"
        @click="runScript(x)"
        v-else-if="x.script">
        <div class="module-info">
          <!-- emoji -->
          <fts-moji :size="20" :emoji="x.emoji" class="module-emoji" />
          <!-- name -->
          <fts-tippy 
            trigger-class="module-name"
            :options="{arrow: false, placement: 'top-start'}" 
            :content="x.name"
            class="module-name-wrapper">
            <template #trigger>{{x.name}}</template>
          </fts-tippy>
        </div>
      </div>
      <!-- design mode -->
      <div
        class="module-wrapper"
        :style="moduleChild"
        :key="x.id"
        @mouseover="onHover(x, true)"
        @mouseleave="onHover(x, false)"
        v-else-if="mode == 'design'"
      >
        <div class="module-info">
          <!-- emoji -->
          <fts-emoji
            class="module-emoji"
            v-model="x.emoji"
            :appendToBody="true"
            :toggleSize="20"
            @click.prevent
          />
          <!-- name -->
          <fts-contenteditable
            class="module-contenteditable"
            v-model="x.name"
            @click.prevent
          />
        </div>
        <!-- actions -->
        <div class="module-action-container">
          <!-- design -->
          <fts-btn
            sm
            icon
            color="secondary"
            class="module-action stopPropagation"
            box-shadow-none
            @click="openListDesign(x)"
            v-if="x.hover && action"
          >
            <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
          </fts-btn>
        </div>
      </div>
      <!-- module / menu -->
      <router-link
        :to="listViewRoute(x.id)"
        custom
        v-slot="{ navigate, href, route }"
        class="module-wrapper"
        :class="{ active: x.id == (list.id || module.id) }"
        :style="moduleChild"
        :key="x.id"
        @mouseover.native="onHover(x, true)"
        @mouseleave.native="onHover(x)"
        v-else
      >
        <a :href="href" @click.prevent="openListView(navigate, route)">
          <div class="module-info">
            <!-- emoji -->
            <fts-moji :size="20" :emoji="x.emoji" class="module-emoji" />
            <!-- name -->
            <fts-tippy
              trigger-class="module-name"
              :options="{arrow: false, placement: 'top-start'}"
              :content="x.name"
              class="module-name-wrapper"
            >
              <template #trigger>{{x.name}}</template>
            </fts-tippy>
            <!-- profile -->
            <div class="module-tag" v-if="x.isProfile">
              <font-awesome-icon :icon="['fas', 'user']" fixed-width />
            </div>
          </div>
          <!-- actions -->
          <div class="module-action-container">
            <!-- design:module -->
            <template v-if="isAdminUser && mode != 'design' && x.module">
              <router-link
                :to="designRoute(x.module)"
                custom 
                v-slot="{ navigate, href, route }"
                v-if="x.hover"
              >
                <fts-btn
                  sm
                  icon
                  color="secondary"
                  class="module-action stopPropagation"
                  box-shadow-none
                  tag="a"
                  :href="href"
                  @click="navigate(route)"
                >
                  <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
                </fts-btn>
              </router-link>
            </template>
            <!-- design:list -->
            <router-link
              :to="designRoute(parentId, x.id)"
              custom
              v-slot="{ navigate, href, route }"
              v-else-if="parentId && isAdminUser && mode != 'design' && x.hover"
            >
              <fts-btn
                sm
                icon
                color="secondary"
                class="module-action stopPropagation"
                box-shadow-none
                tag="a"
                :href="href"
                @click="navigate(route)"
              >
                <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
              </fts-btn>
            </router-link>
            <!-- create -->
            <fts-btn
              sm
              icon
              color="primary"
              class="module-action stopPropagation"
              box-shadow-none
              @click="openCreateForm(x)"
              v-if="creatable(x.id) && mode != 'design' && x.hover"
            >
              <font-awesome-icon :icon="['fas', 'plus']" />
            </fts-btn>
          </div>
        </a>
      </router-link>
      <!-- child -->
      <fts-transition-expand :key="`${x.id || ind}-child`">
        <fts-module
          :mode="mode"
          child
          :modules="x.children"
          :deep="deep + 1"
          :parent-id="getParentId(x)"
          v-if="x.open" />
      </fts-transition-expand>
    </template>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from "../store/clientStore";
import commons from "../commons/commons";

import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSMoji from "../elements/FTSMoji";
import FTSBtn from "../elements/FTSBtn";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSEmoji from "../elements/FTSEmoji";
import FTSTippy from "../elements/FTSTippy";

export default {
  name: "fts-module",
  props: {
    mode: String,
    modules: Array,
    deep: {
      type: Number,
      default: 0,
    },
    child: Boolean,
    parentId: [String, Number],
    action: Boolean,
  },
  components: {
    "fts-contenteditable": FTSContenteditable,
    "fts-emoji": FTSEmoji,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-moji": FTSMoji,
    "fts-btn": FTSBtn,
    "fts-tippy": FTSTippy,
  },
  data: () => ({}),
  methods: {
    isGroup(x) {
      return (
        x.type === "group" ||
        (x.children && x.children.length && x.children[0].default !== true)
      );
    },
    openChildren(x) {
      if (commons.elementStopPropagation()) 
        return;
      else if (x.open === undefined) 
        this.$set(x, "open", true);
      else 
        x.open = !x.open;
    },
    onHover(x, visible) {
      if (visible)
        this.$set(x, "hover", visible)
      else
        this.$delete(x, "hover")
    },
    runScript(x) {
      eval(x.script);
    },
    creatable(id) {
      const findModule = clientStore.state.workspaceModules.find(
        (x) => x.id == id
      );
      return findModule && findModule.canCreate;
    },
    getParentId(x) {
      if (x.type !== "group") return x.id;
    },
    listViewRoute(x) {
      if (this.mode === "design")
        return;
      else if (this.parentId)
        return {
          name: 'list-view',
          params: { moduleId: commons.encodeids(this.parentId) },
          query: { l: x }
        }
      else
        return {
          name: 'list-view',
          params: { moduleId: commons.encodeids(x) }
        }
    },
    designRoute(moduleId, listId) {
      let route = {
        name: 'studio',
        query: { id: commons.encodeids(moduleId) }
      }

      if (listId)
        route.query = {
          ...route.query,
          t: 'menu',
          tid: listId
        }

      return route;
    },
    openListView(navigate, route) {
      if (commons.elementStopPropagation())
        return;
      else
        navigate(route);
    },
    openCreateForm(item) {
      window.eventBus.$emit("quickViewData", {
        activityId: "create",
        vectorId: item.id,
      });
    },
    openListDesign(x) {
      if (this.mode == "design") {
        this.$delete(x, "hover");
        this.$emit("list", x.id);
      }
    },
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    menus() {
      return this.child ? this.modules : commons.visibleMenu(clientStore.state.workspace.menus);
    },
    moduleChild() {
      return {
        "padding-left": `calc(${this.deep * 20}px + ${
          this.deep * 0.75
        }rem + 1rem)`,
      };
    },
    module() {
      return clientStore.state.module || {};
    },
    moduleId() {
      return clientStore.getters.moduleId;
    },
    list() {
      return clientStore.state.list || {};
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-module {
  .module-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    text-decoration: unset;
    color: var(--drawer);
    transition: background 0.3s ease-in-out;
    cursor: pointer;
    &:not(.module-group) {
      &:hover {
        background: var(--drawer-hover-background);
        color: var(--drawer-hover);
      }
      &.active {
        background: var(--drawer-active-background);
        color: var(--drawer-active);
      }
    }
    &.module-group {
      &.open {
        .module-toggle {
          transform: rotate(90deg);
        }
      }
    }
    .module-info {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: center;
      .module-emoji {
        margin-right: 0.575rem;
      }
      .module-name-wrapper {
        min-width: 0;
        .module-name {
          @include text-overflow-ellipsis;
          font-size: 1.15rem;
        }
      }
      .module-contenteditable {
        margin-right: .575rem;
        font-size: 1.15rem;
      }
      .module-tag {
        margin-left: .575rem;  
        padding: .1rem .175rem;
        font-size: .675rem;
        border-radius: .25rem;
        background: var(--drawer-secondary-background);
        color: var(--drawer-secondary);
      }
    }
    .module-action-container {
      position: absolute;
      right: 1rem;
      .module-action {
        &:not(:first-child) {
          margin-left: .275rem;
        }
        z-index: 1;
      }
    }
    .module-toggle {
      margin-left: .575rem;
      color: inherit;
      transition: all 0.3s;
    }
  }
}
</style>